/**
 * OwnerActionPage
 * ---------------
 * Public landing page that powers the HMAC-tokenized one-click action links
 * embedded in owner-facing emails (Press Start, Already Ordered, Approve /
 * Reject Change Order). No login required — the token in the URL is the auth.
 *
 * Flow:
 *   GET /api/public/owner-action/:token  → fetch the action context
 *   POST .../execute                     → perform the action (idempotent)
 *
 * Route: /owner-action/:token (public, no ProtectedRoute wrapper)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  start_project: {
    color: '#059669',
    label: 'Start Project',
    cta: 'Press Start — break ground now',
    headline: 'Ready to break ground?',
  },
  already_ordered: {
    color: '#0369a1',
    label: 'Already Ordered',
    cta: 'Mark as already ordered',
    headline: 'Mark this as already ordered',
  },
  approve_change_order: {
    color: '#059669',
    label: 'Approve Change Order',
    cta: 'Approve this change order',
    headline: 'Approve change order?',
  },
  reject_change_order: {
    color: '#b91c1c',
    label: 'Reject Change Order',
    cta: 'Reject this change order',
    headline: 'Reject change order?',
  },
};

export default function OwnerActionPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/owner-action/${token}`);
        if (!cancelled) setCtx(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'This link could not be verified.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleExecute = async () => {
    setExecuting(true);
    try {
      const r = await axios.post(`${API}/public/owner-action/${token}/execute`);
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not complete this action.');
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          Verifying link…
        </div>
      </div>
    );
  }

  if (error && !ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="owner-action-error">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-1">Link can't be used</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const meta = KIND_META[ctx?.kind] || KIND_META.start_project;

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="owner-action-success">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: meta.color }} />
          <h1 className="text-lg font-bold text-slate-900 mb-1">
            {result.already_done ? 'Already done' : 'Done — thanks!'}
          </h1>
          <p className="text-sm text-slate-600 mb-5">{result.message}</p>
          {result.redirect_path && (
            <Button
              size="sm"
              onClick={() => navigate(result.redirect_path)}
              data-testid="owner-action-open-project-btn"
              className="text-xs"
            >
              Open project <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-8" data-testid="owner-action-page">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-7 pt-7 pb-4 border-b border-slate-100">
          <div
            className="text-[10px] font-bold uppercase tracking-wider mb-1"
            style={{ color: meta.color }}
          >
            DrawReady · {meta.label}
          </div>
          <h1 className="text-xl font-bold text-slate-900">{meta.headline}</h1>
          {ctx.project_name && (
            <p className="text-sm text-slate-600 mt-1.5">
              {ctx.project_name}
              {ctx.project_address && (
                <span className="text-slate-400"> · {ctx.project_address}</span>
              )}
            </p>
          )}
        </div>

        <div className="px-7 py-5 space-y-4">
          {ctx.target_label && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                {ctx.kind.startsWith('approve_') || ctx.kind.startsWith('reject_')
                  ? 'Change order'
                  : 'Action'}
              </div>
              <div className="text-sm font-semibold text-slate-900">{ctx.target_label}</div>
              {ctx.target_meta?.contractor && (
                <div className="text-xs text-slate-500 mt-1">From {ctx.target_meta.contractor}</div>
              )}
              {(ctx.target_meta?.amount_delta || ctx.target_meta?.days_delta) ? (
                <div className="mt-2 text-xs text-slate-600 space-x-3">
                  {ctx.target_meta?.amount_delta ? (
                    <span>
                      Cost:{' '}
                      <strong style={{ color: ctx.target_meta.amount_delta > 0 ? '#dc2626' : '#059669' }}>
                        {ctx.target_meta.amount_delta > 0 ? '+' : ''}
                        ${Number(ctx.target_meta.amount_delta).toLocaleString()}
                      </strong>
                    </span>
                  ) : null}
                  {ctx.target_meta?.days_delta ? (
                    <span>
                      Schedule:{' '}
                      <strong>
                        {ctx.target_meta.days_delta > 0 ? '+' : ''}
                        {ctx.target_meta.days_delta} days
                      </strong>
                    </span>
                  ) : null}
                </div>
              ) : null}
              {ctx.target_meta?.description && (
                <p className="mt-2 text-xs text-slate-600 whitespace-pre-wrap">
                  {ctx.target_meta.description}
                </p>
              )}
            </div>
          )}

          {ctx.already_done && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800">
                {ctx.already_done_label || 'This action has already been completed.'} You can still
                tap below — we'll just re-confirm the existing state.
              </div>
            </div>
          )}

          <Button
            size="lg"
            disabled={executing}
            onClick={handleExecute}
            className="w-full text-white font-semibold"
            style={{ background: meta.color }}
            data-testid="owner-action-execute-btn"
          >
            {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {meta.cta}
          </Button>

          <p className="text-[10px] text-slate-400 text-center">
            This link is signed and unique to you · expires automatically.
          </p>
        </div>
      </div>
    </div>
  );
}
